<template>
  <nav class="relative z-10 w-full bg-background border-b border-border">
    <div class="px-4">
      <div class="hidden lg:flex h-16 items-center justify-between">
        <!-- Navigation Links -->
        <div class="hidden lg:flex lg:items-center lg:gap-8">
          <div v-for="link in navigationLinks" :key="link.url">
            <!-- Models Mega Menu -->
            <Popover v-if="link.name === 'Models'">
              <template #default="{ open, close }">
                <PopoverButton 
                  class="flex items-center gap-x-1 text-lg text-foreground hover:text-primary"
                  :class="{ 'text-primary': open }"
                >
                  {{ link.name }}
                  <ChevronDownIcon 
                    class="h-5 w-5 flex-none text-muted-foreground transition-transform duration-200" 
                    :class="{ 'rotate-180': open }"
                    aria-hidden="true" 
                  />
                </PopoverButton>

                <transition
                  enter-active-class="transition ease-out duration-200"
                  enter-from-class="opacity-0 translate-y-1"
                  enter-to-class="opacity-100 translate-y-0"
                  leave-active-class="transition ease-in duration-150"
                  leave-from-class="opacity-100 translate-y-0"
                  leave-to-class="opacity-0 translate-y-1"
                >
                  <PopoverPanel 
                    class="absolute inset-x-0 left-0 top-full z-50 bg-background shadow-lg"
                  >
                    <div class="border-t border-border" />
                    <div class="mx-auto py-8">
                      <VehicleModelsMegaMenu @close-request="close" />
                    </div>
                  </PopoverPanel>
                </transition>
              </template>
            </Popover>

            <!-- Vehicles Link -->
            <NuxtLink 
              v-else-if="link.name === 'Vehicles'" 
              :to="{ path: link.url, query: currentFilterQuery }"
              class="text-lg text-foreground hover:text-primary"
            >
              {{ link.name }}
            </NuxtLink>

            <!-- Regular Submenu -->
            <Popover v-else-if="link.submenu?.rendered">
              <template #default="{ open, close }">
                <PopoverButton 
                  class="flex items-center gap-x-1 text-lg text-foreground hover:text-primary"
                  :class="{ 'text-primary': open }"
                >
                  {{ link.name }}
                  <ChevronDownIcon 
                    class="h-5 w-5 flex-none text-muted-foreground transition-transform duration-200" 
                    :class="{ 'rotate-180': open }"
                    aria-hidden="true" 
                  />
                </PopoverButton>

                <transition
                  enter-active-class="transition ease-out duration-200"
                  enter-from-class="opacity-0 translate-y-1"
                  enter-to-class="opacity-100 translate-y-0"
                  leave-active-class="transition ease-in duration-150"
                  leave-from-class="opacity-100 translate-y-0"
                  leave-to-class="opacity-0 translate-y-1"
                >
                  <PopoverPanel 
                    class="absolute inset-x-0 left-0 top-full z-50 bg-background shadow-lg"
                    @click="handleSubmenuClick($event, close)"
                  >
                    <div class="border-t border-border" />
                    <div class="container mx-auto py-8">
                      <div ref="submenuContentRef" class="submenu-content" v-html="processedSubmenu(link.submenu.rendered)" />
                    </div>
                  </PopoverPanel>
                </transition>
              </template>
            </Popover>

            <!-- Regular Link -->
            <NuxtLink 
              v-else 
              :to="link.url"
              class="text-lg text-foreground hover:text-primary"
            >
              {{ link.name }}
            </NuxtLink>
          </div>
        </div>


      </div>
    </div>


  </nav>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import { useSiteStore } from '~/stores/site'
import { useModelsStore } from '~/stores/models'
import { useVehiclesStore } from '~/stores/vehicles'
import { useRouter } from 'vue-router'
import { useNuxtApp } from 'nuxt/app'

// Define interface for navigation links
interface NavigationLink {
  name: string;
  url: string;
  submenu?: {
    rendered: string;
  };
}

interface SiteStoreWithSocial {
  logo?: string;
  name?: string;
  phone?: string;
  social?: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    [key: string]: string | undefined;
  };
  navigation?: NavigationLink[];
}

const siteStore = useSiteStore() as unknown as SiteStoreWithSocial
const modelsStore = useModelsStore()
const vehiclesStore = useVehiclesStore()
const router = useRouter()
const submenuContentRef = ref<HTMLElement | null>(null)
const popoverCloseCallbacks = ref<Function[]>([])
const { $colorMode: colorMode } = useNuxtApp()

// Check if social media links exist 
const hasSocialLinks = computed(() => {
  return !!(siteStore.social?.facebook || siteStore.social?.twitter || siteStore.social?.instagram);
})

// Interface for range filter values
interface RangeFilterValue {
  min: number;
  max: number;
  type: 'range';
}

// Computed property for current filter query
const currentFilterQuery = computed(() => {
  const query: Record<string, string | string[]> = {}
  
  // Add search query if present
  if (vehiclesStore.activeFilters.search_keywords) {
    const searchKeywords = vehiclesStore.activeFilters.search_keywords
    if (searchKeywords.type === 'text') {
      query.search_keywords = searchKeywords.value
    }
  }
  
  // Add other active filters
  Object.entries(vehiclesStore.activeFilters).forEach(([key, value]) => {
    if (value) {
      if (Array.isArray(value) && value.length > 0) {
        query[key] = value.join(',')
      } else if (key === 'perweek' && typeof value === 'object' && value !== null && value.type === 'range') {
        // For range filters like budget, add as separate min/max parameters
        const rangeValue = value as RangeFilterValue
        query[`${key}_min`] = String(rangeValue.min)
        query[`${key}_max`] = String(rangeValue.max)
      }
    }
  })
  
  return query
})

// Navigation links
const navigationLinks = computed<NavigationLink[]>(() => {
  const links: NavigationLink[] = [
    { name: 'Home', url: '/' },
    { name: 'Models', url: '/models' },
    { name: 'Vehicles', url: '/vehicles' },
    { name: 'Cars for Finance', url: '/cars-for-finance' },
    { name: 'Cars For Sale', url: '/cars-for-sale' },
    { name: 'Services', url: '/services' },
    { name: 'About', url: '/about' },
    { name: 'Contact', url: '/contact' }
  ]
  
  // Add custom links from site configuration if available
  const siteNavigation = siteStore.navigation
  if (siteNavigation?.length) {
    return siteNavigation.map((link: NavigationLink) => {
      // Check for custom submenu from site configuration
      if (link.submenu?.rendered) {
        return {
          ...link,
          submenu: {
            ...link.submenu,
            rendered: link.submenu.rendered
          }
        }
      }
      return link
    })
  }
  
  return links
})

// Process submenu HTML to convert relative links
const processedSubmenu = (html: string): string => {
  if (!html) return ''

  // Create a temporary DOM element to parse the HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  // Process all links in the temporary DOM
  const links = tempDiv.querySelectorAll('a')
  links.forEach(link => {
    const href = link.getAttribute('href')
    
    // Check if it's a relative link (doesn't start with http, https, //, #, tel:, mailto:)
    if (href && !href.match(/^(https?:\/\/|\/\/|#|tel:|mailto:)/)) {
      // Add a special data attribute to identify this as a relative link
      link.setAttribute('data-nuxt-link', 'true')
      link.setAttribute('data-href', href)
      
      // Remove the href attribute to prevent default navigation
      link.removeAttribute('href')
    }
  })

  // Apply class transformations
  const classMap: Record<string, string> = {
    // 'uk-container': 'mx-auto',
    // 'uk-container-large': 'mx-auto md:max-w-7xl',
    'uk-grid': 'grid grid-cols-1 md:grid-cols-3 gap-4',
    'uk-flex': 'flex',
    'uk-text-meta': 'text-xs',
    'uk-flex-middle': 'items-center',
    'uk-width-1-1': 'w-full',
    'uk-width-1-3@l': 'w-full',
    'uk-padding-small': 'p-4',
    'uk-text-center': 'text-center',
    'uk-link-reset': 'no-underline text-foreground hover:text-primary transition-colors duration-200',
    'uk-transition-scale-up uk-transition-opaque': 'transform transition-transform hover:scale-105 duration-200',
    'uk-margin-remove': 'm-0',
    'uk-text-meta uk-text-muted': 'text-sm text-muted-foreground',
    'moon-icon': 'inline-block w-8 h-8 mb-3',
    'icon-nav-title': 'text-lg font-semibold mb-2',
    'icon-sub-text': 'text-sm text-muted-foreground'
  }

  // Apply class transformations by iterating through elements with specific classes
  // Instead of using querySelectorAll with potentially invalid selectors
  const allElements = tempDiv.querySelectorAll('*')
  allElements.forEach(el => {
    if (el.classList) {
      const classesToRemove: string[] = []
      const classesToAdd: string[] = []
      
      // Check each class on the element
      el.classList.forEach(className => {
        if (classMap[className]) {
          classesToRemove.push(className)
          classesToAdd.push(...classMap[className].split(' '))
        }
      })
      
      // Remove old classes
      classesToRemove.forEach(cls => el.classList.remove(cls))
      
      // Add new classes
      classesToAdd.forEach(cls => el.classList.add(cls))
    }
  })

  return tempDiv.innerHTML
}

// Handle clicks on submenu items
const handleSubmenuClick = (event: MouseEvent, closePopover: Function) => {
  const target = event.target as HTMLElement
  const link = target.closest('[data-nuxt-link="true"]')
  
  if (link) {
    event.preventDefault()
    const href = link.getAttribute('data-href')
    if (href) {
      // Close the popover before navigation
      closePopover()
      // Use Nuxt's router to navigate
      navigateTo(href)
    }
  }
}



// Close all popovers when route changes
const closeAllPopovers = () => {
  popoverCloseCallbacks.value.forEach(close => close())
}

onMounted(async () => {
  if (process.client) {
    // Listen for route changes to close menus
    router.beforeEach((to, from, next) => {
      closeAllPopovers()
      next()
    })

    // Use event delegation to handle clicks on relative links
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      const link = target.closest('[data-nuxt-link="true"]')
      
      if (link) {
        event.preventDefault()
        const href = link.getAttribute('data-href')
        if (href) {
          // Use Nuxt's router to navigate
          navigateTo(href)
        }
      }
    })

    // Register popover close functions
    const popovers = document.querySelectorAll('[data-headlessui-state="open"]')
    popovers.forEach(popover => {
      const button = popover.querySelector('[aria-expanded="true"]')
      if (button) {
        const closeEvent = new Event('click')
        popoverCloseCallbacks.value.push(() => button.dispatchEvent(closeEvent))
      }
    })
  }
})

onBeforeUnmount(() => {
  // Clean up event listeners
  if (process.client) {
    router.beforeEach(() => {})
  }
})
</script>


